import type {
	CustomQuestionnaireQuestion,
	CustomQuestionnaireQuestionsDto,
} from '#api/customQuestionnaire'

import type {
	QuestionDefinition,
	QuestionOption,
	QuestionType,
} from '../../types'

import { getTranslation } from './utils'

export const convertToQuestions = (
	dto: CustomQuestionnaireQuestionsDto,
	languageCode: string,
): QuestionDefinition[] => {
	if (!dto.questions || dto.questions.length === 0) {
		return []
	}

	return dto.questions.map((question) =>
		convertToQuestion(question, languageCode),
	)
}

const questionTypeMap: Record<
	CustomQuestionnaireQuestion['type'],
	QuestionType
> = {
	SingleSelectQuestion: 'singleSelect',
	MultiSelectQuestion: 'multiSelect',
	ShortTextQuestion: 'textInput',
	NumberInputQuestion: 'numberInput',
	CurrencyInputQuestion: 'currencyInput',
	DocumentUploadQuestion: 'documentUpload',
}

const convertToQuestion = (
	question: CustomQuestionnaireQuestion,
	languageCode: string,
): QuestionDefinition => {
	const questionType = questionTypeMap[question.type]

	const questionLabel = getTranslation(question.label, languageCode)
	const helpText =
		question.helpText && getTranslation(question.helpText, languageCode)

	// Handle options based on question type
	const options: QuestionOption[] | undefined =
		question.type === 'SingleSelectQuestion' ||
		question.type === 'MultiSelectQuestion'
			? question.options.map((option) => ({
					id: option.id,
					value: option.value,
					label: getTranslation(option.label, languageCode),
					subquestions:
						option.subquestions && option.subquestions.length > 0
							? option.subquestions?.map((subq) =>
									convertToQuestion(subq, languageCode),
								)
							: undefined,
				}))
			: undefined

	const step =
		question.type === 'NumberInputQuestion' ||
		question.type === 'CurrencyInputQuestion'
			? question.step
			: undefined

	return {
		id: question.id,
		slug: question.slug,
		formFieldKey: question.id,
		outputKey: question.slug,
		questionType,
		label: questionLabel,
		options,
		validations: getValidations(question),
		helpText,
		step,
	}
}

const getValidations = (question: CustomQuestionnaireQuestion) => {
	return {
		required: question.isRequired || false,
		minLength:
			question.type === 'ShortTextQuestion' && question.minLength > 0
				? question.minLength
				: undefined,
		min:
			question.type === 'NumberInputQuestion'
				? (question.min ?? undefined)
				: undefined,
		max:
			question.type === 'NumberInputQuestion'
				? (question.max ?? undefined)
				: undefined,
	}
}
